import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { CustomEntity } from 'src/app/models/customEntity.model';
import { CustomField } from 'src/app/models/customField.model';
import { Lookup } from 'src/app/models/lookup.model';
import { CustomEntityService } from 'src/app/services/customEntity.service';
import { CustomObjectService } from 'src/app/services/customObject.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { CustomFieldTypeEnum } from 'src/app/enums/customFieldType.enum';
import { isCustomFieldKey } from 'src/app/shared/form/custom-field-helpers';

export interface EmailFormState {
  recipient: string;
  ccRecipients: string[];
  subject: string;
  message: string;
  recipientInput: string;
  ccInput: string;
}

export interface EmployeeFieldProperties {
  firstName: string;
  lastName: string;
  email: string;
  employeeType: string;
  position: string;
  supervisorEmail: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPLOYEE_PAGINATION = { pageIndex: 0, pageSize: 50, scrollBatchSize: 50 };

const FIELD_TYPE_MAP = new Map<CustomFieldTypeEnum, keyof EmployeeFieldProperties>([
  [CustomFieldTypeEnum.HrisFirstName, 'firstName'],
  [CustomFieldTypeEnum.HrisLastName, 'lastName'],
  [CustomFieldTypeEnum.HrisEmailId, 'email'],
  [CustomFieldTypeEnum.HrisEmployeeType, 'employeeType'],
  [CustomFieldTypeEnum.HrisPosition, 'position'],
  [CustomFieldTypeEnum.HrisSupervisorEmail, 'supervisorEmail']
]);

@Injectable()
export class EmailCompositionService implements OnDestroy {
  employees: CustomEntity[] = [];
  filteredEmployees: CustomEntity[] = [];
  filteredCcEmployees: CustomEntity[] = [];
  customFields: CustomField[] = [];
  employeeFields: EmployeeFieldProperties = {
    firstName: '', lastName: '', email: '',
    employeeType: '', position: '', supervisorEmail: ''
  };

  coId: string | number | null = null;
  currentPage = 0;
  hasMore = true;
  isLoading = false;

  private readonly destroy$ = new Subject<void>();
  private readonly recipientSearch$ = new Subject<string>();
  private readonly ccSearch$ = new Subject<string>();
  private loadingPromise: Promise<void> | null = null;
  private organizationLevelsMap = new Map<string, string>();

  constructor(
    private customEntityService: CustomEntityService,
    private customObjectService: CustomObjectService,
    private organizationLevelService: OrganizationLevelService
  ) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupSearch(
    onRecipientSearch: (term: string) => void,
    onCcSearch: (term: string) => void
  ): void {
    this.recipientSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(onRecipientSearch);

    this.ccSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(onCcSearch);
  }

  triggerRecipientSearch(term: string): void {
    this.recipientSearch$.next(term);
  }

  triggerCcSearch(term: string): void {
    this.ccSearch$.next(term);
  }

  async loadCustomFieldsAsync(): Promise<void> {
    if (this.loadingPromise) return this.loadingPromise;
    if (this.customFields.length > 0 && this.coId) return;

    this.loadingPromise = this.fetchAndMapCustomFields();
    return this.loadingPromise;
  }

  private async fetchAndMapCustomFields(): Promise<void> {
    try {
      const customObjects = await firstValueFrom(this.customObjectService.getAll());
      const employeeType = customObjects?.find(t => t.name === 'Employee');
      if (!employeeType) return;

      this.coId = employeeType.id;
      const fields = await firstValueFrom(
        this.customObjectService.getCustomObjectFields(employeeType.id)
      );

      if (fields) {
        this.customFields = fields.filter(f => isCustomFieldKey(f.property));
        this.mapFieldProperties(fields);
      }
    } finally {
      this.loadingPromise = null;
    }
  }

  private mapFieldProperties(fields: CustomField[]): void {
    FIELD_TYPE_MAP.forEach((targetProp, fieldType) => {
      const field = fields.find(f => f.type === fieldType);
      if (field) this.employeeFields[targetProp] = field.property;
    });
  }

  async loadEmployeesAsync(searchTerm = '', target: 'recipient' | 'cc' | 'both' = 'both'): Promise<void> {
    if (!this.coId) return;

    try {
      const result = await this.fetchEmployees(searchTerm, EMPLOYEE_PAGINATION.pageIndex, EMPLOYEE_PAGINATION.pageSize);
      if (!result?.data) return;

      this.employees = result.data;
      this.updateFilteredLists(result.data, target);
      this.hasMore = result.data.length === EMPLOYEE_PAGINATION.pageSize;
      this.currentPage = 0;
    } catch (error) {
      this.clearAllLists();
    }
  }

  async loadEmployeesWithBoundAsync(
    searchTerm = '',
    target: 'recipient' | 'cc' | 'both' = 'both',
    initialBoundEmployee: CustomEntity | null = null
  ): Promise<void> {
    if (!this.coId) return;

    try {
      const result = await this.fetchEmployees(searchTerm, EMPLOYEE_PAGINATION.pageIndex, EMPLOYEE_PAGINATION.pageSize);
      if (!result?.data) return;

      this.employees = result.data;
      const finalList = this.buildListWithBoundEmployee(result.data, initialBoundEmployee, target, searchTerm);

      if (target === 'recipient' || target === 'both') this.filteredEmployees = finalList;
      if (target === 'cc' || target === 'both') {
        this.filteredCcEmployees = target === 'cc' ? [...result.data] : finalList;
      }

      this.hasMore = result.data.length === EMPLOYEE_PAGINATION.pageSize;
      this.currentPage = 0;
    } catch (error) {
      this.clearAllLists();
    }
  }

  async loadMoreEmployeesAsync(searchTerm = ''): Promise<void> {
    if (!this.hasMore || this.isLoading || !this.coId) return;

    this.isLoading = true;
    this.currentPage++;

    try {
      const result = await this.fetchEmployees(searchTerm, this.currentPage, EMPLOYEE_PAGINATION.scrollBatchSize);
      if (!result?.data) return;

      this.employees = [...this.employees, ...result.data];
      this.filteredEmployees = [...this.filteredEmployees, ...result.data];
      this.filteredCcEmployees = [...this.filteredCcEmployees, ...result.data];
      this.hasMore = result.data.length === EMPLOYEE_PAGINATION.scrollBatchSize;
    } finally {
      this.isLoading = false;
    }
  }

  async loadEmployeeByIdAsync(employeeId: string, organizationLevels?: Lookup[]): Promise<CustomEntity | null> {
    if (!this.coId) return null;

    const existing = this.employees.find(e => e.id === employeeId);
    if (existing) return existing;

    try {
      const employee = await firstValueFrom(
        this.customEntityService.getCustomEntity(employeeId, this.coId)
      );

      if (employee) {
        if (employee.organizationLevelId && !employee.organizationLevelName) {
          employee.organizationLevelName = await this.getOrganizationLevelName(
            employee.organizationLevelId,
            organizationLevels
          );
        }

        this.employees.push(employee);
        this.filteredEmployees.push(employee);
        this.filteredCcEmployees.push(employee);
        return employee;
      }
    } catch (error) {
    }

    return null;
  }

  async getOrganizationLevelName(organizationLevelId: string, organizationLevels?: Lookup[]): Promise<string> {
    const cached = this.organizationLevelsMap.get(organizationLevelId);
    if (cached) return cached;

    const fromProvided = organizationLevels?.find(org => org.id === organizationLevelId);
    if (fromProvided?.name) {
      this.organizationLevelsMap.set(organizationLevelId, fromProvided.name);
      return fromProvided.name;
    }

    try {
      const orgLevel = await firstValueFrom(this.organizationLevelService.getById(organizationLevelId));
      if (orgLevel?.name) {
        this.organizationLevelsMap.set(organizationLevelId, orgLevel.name);
        return orgLevel.name;
      }
    } catch (error) {
    }

    return '';
  }

  findFieldByType(type: CustomFieldTypeEnum): CustomField | undefined {
    return this.customFields.find(f => f.type === type);
  }

  getEmployeeEmail(employee: CustomEntity): string {
    return employee?.[this.employeeFields.email] || '';
  }

  getEmployeeFullName(employee: CustomEntity): string {
    if (!employee) return '';
    const firstName = employee[this.employeeFields.firstName] || '';
    const lastName = employee[this.employeeFields.lastName] || '';
    return `${firstName} ${lastName}`.trim() || '-';
  }

  isValidEmail(email: string): boolean {
    return EMAIL_REGEX.test(email);
  }

  createEmptyForm(): EmailFormState {
    return {
      recipient: '',
      ccRecipients: [],
      subject: '',
      message: '',
      recipientInput: '',
      ccInput: ''
    };
  }

  private async fetchEmployees(searchTerm: string, pageIndex: number, pageSize: number): Promise<any> {
    return firstValueFrom(
      this.customEntityService.get(
        searchTerm,
        'id',
        'asc',
        pageIndex,
        pageSize,
        '',
        new HttpHeaders({ coid: this.coId!.toString() })
      )
    );
  }

  private updateFilteredLists(data: CustomEntity[], target: 'recipient' | 'cc' | 'both'): void {
    if (target === 'recipient' || target === 'both') this.filteredEmployees = [...data];
    if (target === 'cc' || target === 'both') this.filteredCcEmployees = [...data];
  }

  private buildListWithBoundEmployee(
    data: CustomEntity[],
    boundEmployee: CustomEntity | null,
    target: string,
    searchTerm: string
  ): CustomEntity[] {
    if (target !== 'recipient' || !boundEmployee || searchTerm.trim()) {
      return [...data];
    }

    const alreadyInList = data.some(e => e.id === boundEmployee.id);
    return alreadyInList ? [...data] : [boundEmployee, ...data];
  }

  private clearAllLists(): void {
    this.employees = [];
    this.filteredEmployees = [];
    this.filteredCcEmployees = [];
  }
}